import React, { useEffect, useState, useRef, createRef, useContext, createContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import Truncate from "react-truncate";

import cx from 'classnames';
import _ from 'lodash';
import dateformat from "dateformat";
import {Button, Modal } from "react-bootstrap";

import { TrendsLineChart } from './charts'

import { fetchPageMetricsForType, fetchStoryboardLeadsTimeSeries } from 'actions/storyboardReports';
import { convertDatesToTimezone } from 'services/report.service';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import { leadReportHeader } from 'constants/leadReportData';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useSortable, SortableContext, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { VisitorProfileView, LeadAnalyticsHeader, TableInfo, EmptyMessage, LoadingMessage } from '.';
import { TableFooter } from './tableFooter';
import storyboardRoleService from 'services/storyboardRoles.service';
import { defaultReportType } from '../constants/reports';
import { formatDurationWithLabel } from '../reporthelpers';

import styles from '../report.module.scss';

export function LeadAnalytics(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const leadsLoad = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.leads.loading`, true));
  const leads = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.leads.items`, []));
  const views = useSelector(state => _.get(state, 'storyboards.storyboard.reports.leadTimeSeries.items'));
  const pagination = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.leads.pagination`));
  const loading = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.leads.loading`, false));
  const [currentChart, setCurrentChart] = useState("table_view");
  const reportType = useSelector(state => _.get(state, 'storyboards.storyboard.reports.reportType', defaultReportType));
  const { isLeadReportPage = false } = props;

  const updatedLeadReportHeader = useMemo(() => {
    if(leadsLoad) return []
      leads.map(lead => {
        lead.fields.map(field => {
          const exists = field && leadReportHeader.some(header => header.field === field.field);

          if (!exists && field && field.uid == null) {
            leadReportHeader.push({
              name: field.name,
              field: field.field,
              values: field.values,
              uid: field.uid
            });
          }
        });
      });
      return leadReportHeader;
  }, [leads,leadsLoad]);

  useEffect(() => {
    if (props.date) {
      handlePageChange(0);

      var request = {
        storyboard: storyboardId,
        reportType: isLeadReportPage ? defaultReportType : reportType,
        ...convertDatesToTimezone(props.date),
      }

      dispatch(fetchStoryboardLeadsTimeSeries(request));
    }
  }, [props.date, reportType]);

  const handlePageChange = (page) => {
    var request = {
      storyboard: storyboardId,
      metricType: "leads",
      reportType,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchPageMetricsForType(request, page, 5));
  }

  const handleChartChange = (value) => {
    setCurrentChart(value)
  }

  return (
    <div className={styles.leadsAnalyticsWrapper}>
      <div className={styles.leadsAnalyticsContainer}>
        <TableInfo
          name="Lead Analytics"
          pagination={pagination} />
        {currentChart == "table_view" && leads.length == 0 && <LeadAnalyticsHeader header={updatedLeadReportHeader} storyboardId={storyboardId} />}

        {(leads && leads.length == 0) && loading &&
          <LoadingMessage />
        }

        {(leads && leads.length <= 0) && !loading &&
          <EmptyMessage />
        }

        {currentChart == "table_view" && !loading && (leads && leads.length > 0) &&
          <LeadReportView storyboardId={storyboardId} loading={loading} reportDate={props.date} showProfile={props.showProfile} leads={updatedLeadReportHeader}/>
        }

        {currentChart == "graph_view" &&
          <div className={cx(styles.graphFullView, { [styles.emptyGraph]: (views && views.length == 0) })}>
            <TrendsLineChart data={views} dataKey="views" height={400} />
          </div>
        }
      </div>

      <TableFooter
        currentChart={currentChart}
        disableView={['pie_chart_view']}
        handleChartChange={handleChartChange}
        handlePageChange={handlePageChange}
        pagination={pagination}
        paginationName={"Leads"}
      />
    </div>
  )
}

function LeadReportView({ loading, reportDate, showProfile, index, leads, storyboardId }) {
  const leadsData = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.leads.items`, []));
  const savedFields = localStorage.getItem(`${storyboardId}`);

  const leadFields = leads.map(lead => ({
    fieldId: lead.field,
    uid: lead.uid,
    name: lead.name,
    visible: true,
  }));

  const [fields, setFields] = useState(() => {
    return savedFields ? JSON.parse(savedFields) : leadFields;
  });

  useEffect(() => {
    if (savedFields && leads.length > 0 ) {
      const parsedSavedFields = JSON.parse(savedFields);
      const updatedFields = parsedSavedFields.filter(field => {
        return leads.some(lead => lead.uid === field.fieldId || lead.field === field.fieldId);
      }).map(field => {
        const match = leads.find(lead => lead.uid === field.fieldId || lead.field === field.fieldId);
        return {
          fieldId: field.fieldId,
          uid: field.uid,
          name: match ? match.name : field.name, 
          visible: field.visible
        };
      });

      if (!fields || fields.length !== updatedFields.length || JSON.stringify(fields) !== JSON.stringify(updatedFields)) {
        setFields(updatedFields);
        localStorage.setItem(`${storyboardId}`, JSON.stringify(updatedFields));
      }
    }
  }, [leads]);

  function renderFieldData(leadData, headerField) {
    if (headerField.uid === 'createdDate') {
      return dateformat(leadData.contact[headerField.uid], 'dd mmm yyyy');
    } else if (headerField.uid === 'lastVisitedDate') {
      return dateformat(leadData.lastVisitedDate, 'dd mmm yyyy');
    } else if (headerField.uid === 'timeSpent') {
      return formatDurationWithLabel(leadData[headerField.uid]).value;
    } else if (headerField.uid === 'completion') {
      return `${leadData[headerField.uid]} %`;
    } else if(headerField.uid != null) {
      if (leadData[headerField.uid] !== undefined) {
        return leadData[headerField.uid] || "-";
      }
      if (leadData.contact && leadData.contact[headerField.uid] !== undefined) {
        return leadData.contact[headerField.uid] || "-";
      }
      return "";
    } else {
        const customField = leadData && leadData.fields.find((field) => field.field === headerField.fieldId);
        if (customField) {
          if (typeof customField.values === 'boolean') {
            const value = customField.values.toString() || "-";
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
          }
          if (Array.isArray(customField.values)) {
            return customField.values.length > 0 ? customField.values.join(', ') : "-";
          }
        }

        return customField ? customField.values : "-";
    }
  };

  return (
    <>
      <EditCustomFields fields={fields} updateFields={setFields} storyboardId={storyboardId} savedFields={savedFields} leadFields={leadFields}/>
      <div key={index} className={styles.leadsAnalyticsRow}>
        <div className={styles.tableContainer}>
          <table className={styles.leadsReportTable}>
            <thead>
              <tr>
                {fields.map((field, index) => {
                  const fieldVisibility = field.visible;
                  return fieldVisibility && (
                    <th key={index}>
                      <div className={styles.title}>
                        <TruncatedTextWithTooltip content={field.name} maxWidth={150} placement='top'/>
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>

            <tbody className={styles.tableDetails}>
              {leadsData.map((leadData, rowIndex) => (
                <tr key={rowIndex}>
                  {fields
                    .filter(field => field.visible && leads.some(lead => lead.field === field.fieldId))
                    .map((field, colIndex) => (
                      <td key={colIndex}>
                        {field.uid === 'firstName' ? (
                          <VisitorProfileView
                            data={leadData}
                            reportDate={reportDate}
                            showProfile={showProfile}
                            type='leads'
                            field={field}
                          />
                        ) : (
                          <LeadReportAnalytics
                            data={renderFieldData(leadData, field)}
                            className={styles.fieldData}
                          />
                        )}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>

          {(leads && leads.length <= 0) && !loading &&
            <EmptyMessage />
          }
        </div>
      </div>
    </>
  );
}

const EditCustomFields = ({ fields, updateFields, storyboardId, savedFields, leadFields }) => {
  const [show, setShow] = useState(false);
  const [items, setItems] = useState(fields);
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  const handleToggleVisibility = (index) => {
    const updatedItems = [...items];
    updatedItems[index + 1].visible = !updatedItems[index + 1].visible;
    setItems(updatedItems);
  };

  const handleDone = () => {
    updateFields(items);
    setItems(items);
    localStorage.setItem(`${storyboardId}`, JSON.stringify(items));
    setShow(false);
  };

  useEffect(() => {
    if (!_.isEqual(items, fields)) {
      setItems(fields);
    }
  }, [fields]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => `draggable-${item.fieldId}` === active.id);
      const newIndex = items.findIndex((item) => `draggable-${item.fieldId}` === over.id);
      const updatedItems = arrayMove(items, oldIndex, newIndex);
      setItems(updatedItems);
    }
  };

  const closeModal = () => {
    setShow(false);
    setItems(savedFields ? JSON.parse(savedFields) : leadFields);
  };

  return (
    <>
      <div className={styles.editFields}>
        <Button variant="primary" className={styles.button} onClick={() => setShow(true)} disabled={!storyboardRoleService.canEditStoryboard(storyboard)}>
          <i className={cx('cleverstory-pencil', styles.editFieldIcon)} />
          <span className={styles.name}>Edit Fields</span>
        </Button>
      </div>

      <Modal show={show} className={cx(styles.confirmation, styles.dialog)} backdropClassName={styles.overlay} centered>
        <Modal.Header>
          <div className={styles.editCustomFieldsHeader}>
            <h4 className={styles.heading}>Edit Fields View</h4>
            <p className={styles.subHeading}>You can rearrange the fields to customize your view.</p>
          </div>
          <div className={styles.closeButton} onClick={closeModal}>
            <i className={cx('cleverstory-close', styles.closeButton)} />
          </div>
        </Modal.Header>
        <Modal.Body className={styles.body}>
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((item) => `draggable-${item.fieldId}`)}>
              <div className={styles.fieldsList}>
                {items
                  .filter((field) => field.uid !== 'firstName')
                  .map((field, index) => (
                    <Option
                      id={`draggable-${field.fieldId}`}
                      key={index}
                      field={field}
                      index={index}
                      handleToggleVisibility={handleToggleVisibility}
                    />
                  ))}
              </div>
            </SortableContext>
          </DndContext>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" variant="primary" onClick={handleDone}>Done</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Option = ({ field, index, handleToggleVisibility, id  }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});
  const style = {
    transform: transform
      ? CSS.Transform.toString({
          ...transform,
          scaleX: 1,
          scaleY: 1,
        })
      : undefined,
    transition,
    zIndex: isDragging ? 9999 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };

  return(
    <div ref={setNodeRef} style={style} className={cx({[styles.hiddenFields]: !field.visible }, styles.option, 'cs-draggable-item')} key={index}>
      <div className={styles.value}>
        <span className={cx(styles.dragHandle, 'cs-drag')} {...attributes} {...listeners}  >
          <i className={cx('cleverstory-drag', styles.dragIcon)}/>
        </span>
        <p className={styles.fieldValue}>
          <TruncatedTextWithTooltip content={field.name || '-'} maxWidth={320} placement='top' />
        </p>
        <Button variant="secondary" className={styles.btnShow}>
          <i className={cx({ 'cleverstory-view': field.visible, 'cleverstory-hide': !field.visible })}
            onClick={() => handleToggleVisibility(index)}
          />
        </Button>
      </div>
    </div>
  )
};

function LeadReportAnalytics({ data }) {
  return (
    <>
      <TruncatedTextWithTooltip content={data ? data : "-"} maxWidth={140} placement='top'/>
    </>
  )
}